'use client';

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { setSelectedRoomId, setDisputeId } from '../../../store/slices/chat';
import { socket } from '../../../lib/socket';
import { Message, MessageGroup } from '../../../types/chat';
import ChatContent from '../../../pages/chat/components/ChatContent';
import ChatFooter from '../../../pages/chat/components/ChatFooter';
import SectionWrapper from '../SectionWrapper';
import LoaderSpin from '../../atoms/LoaderSpin';
import { ArrowUp } from 'iconsax-react';
import { message } from 'antd';
import { useGetRequestSummaryQuery } from '../../../store/services/disputeService';
import {
  useGetChatHistoryQuery,
  useGetDisputeChatHistoryQuery,
} from '../../../store/services/chatService';

export default function DisputeChatSection({
  disputeId,
}: {
  disputeId: string;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const userId = user?.adminData?.id;
  const token = useSelector((state: RootState) => state.auth.token);

  const [myMessages, setMyMessages] = useState<MessageGroup[]>([]);
  const [page, setPage] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [typing, setTyping] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Fetch dispute summary details to get the serial code (e.g. DSP-1001)
  const { data: requestSummary } = useGetRequestSummaryQuery(
    { disputeId },
    { skip: !disputeId }
  );

  const serial = requestSummary?.payload?.disputeSerial || disputeId;
  const roomId = requestSummary?.payload?.chatRoomId;

  // Initialize Redux chat state with the disputeId and roomId on mount
  useEffect(() => {
    if (roomId) {
      dispatch(setSelectedRoomId(roomId));
    }
    if (disputeId) {
      dispatch(setDisputeId(disputeId));
    }

    return () => {
      dispatch(setSelectedRoomId(''));
      dispatch(setDisputeId(''));
    };
  }, [dispatch, disputeId, roomId]);

  // Connect socket and handle connection lifecycle
  useEffect(() => {
    if (userId && token) {
      socket.io.opts.query = {
        profileId: userId,
        token: token,
      };
      socket.connect();
    }

    return () => {
      socket.disconnect();
    };
  }, [userId, token]);

  // Fetch grouping messages history using the correct RTK query hook
  const { data: messagesHistory, isLoading: chatLoadingHistory } =
    useGetDisputeChatHistoryQuery(
      {
        roomId: roomId!,
        page,
      },
      {
        skip: !roomId,
        refetchOnMountOrArgChange: true,
      }
    );

  const scrollToBottom = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, []);

  const handleLoadMore = () => {
    if (!isLoadingMore && messagesHistory?.payload?.hasMoreMessages) {
      setIsLoadingMore(true);
      setPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (messagesHistory?.payload?.messagesGrouping) {
      if (page === 0) {
        setMyMessages(messagesHistory.payload.messagesGrouping);
        setTimeout(scrollToBottom, 100);
      } else {
        setMyMessages((prev) => {
          if (!prev) return messagesHistory.payload.messagesGrouping;
          return [...messagesHistory.payload.messagesGrouping, ...prev];
        });
      }
      setIsLoadingMore(false);
    }
  }, [messagesHistory, page, scrollToBottom]);

  // Handle incoming socket messages
  const handleUserMessage = useCallback(
    (msg: string | Message) => {
      const parsedMsg: Message =
        typeof msg === 'string' ? JSON.parse(msg) : msg;

      if (roomId !== parsedMsg.roomId) return;

      const isMsgSent = ['READ', 'SENT', 'DELIVERED'].includes(
        parsedMsg.status
      );

      if (!isMsgSent) {
        message.error(t('messageNotSent'));
        return;
      }

      setMyMessages((prev = []) => [
        ...prev,
        { date: '', messages: [parsedMsg] },
      ]);

      setTimeout(scrollToBottom, 50);
    },
    [roomId, t, scrollToBottom]
  );

  type ChatEvent = {
    isTyping?: boolean;
    type?: 'typing' | 'seen' | 'read';
    roomId?: string;
    messageId?: string;
    id?: string;
    read?: boolean;
  };

  const handleChatEvents = useCallback(
    (event: ChatEvent) => {
      if (roomId !== event.roomId) return;

      if (event.type === 'typing') {
        setTyping(!!event.isTyping);
        return;
      }

      const targetMsgId = event.messageId || event.id;
      const isRead = event.type === 'seen' || event.read;

      if (isRead && targetMsgId) {
        setMyMessages((prev) =>
          prev?.map((group) => ({
            ...group,
            messages: group.messages.map((msg) =>
              msg.id === targetMsgId ? { ...msg, read: true } : msg
            ),
          }))
        );
      }
    },
    [roomId]
  );

  useEffect(() => {
    if (roomId) {
      socket.on('user-messages', handleUserMessage);
      socket.on('chat-events', handleChatEvents);
    }

    return () => {
      socket.off('user-messages', handleUserMessage);
      socket.off('chat-events', handleChatEvents);
    };
  }, [roomId, handleUserMessage, handleChatEvents]);

  // Reset pagination when roomId changes
  useEffect(() => {
    setPage(0);
  }, [roomId]);

  return (
    <SectionWrapper
      title={`${t('components.disputeFeature.threadsOnDispute')} ${serial}`}
      subTitle={t('components.disputeFeature.displayApplicantsRequests')}
    >
      <div className="flex flex-col h-[550px] bg-white dark:bg-black-900 border border-light-input-border dark:border-dark-input-border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
        {/* Chat content scrollable area */}
        <div
          ref={contentRef}
          className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 bg-[#FEF6F2]/10 dark:bg-black-950/20"
        >
          {messagesHistory?.payload?.hasMoreMessages && !chatLoadingHistory && (
            <div className="flex justify-center py-2">
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="flex items-center justify-center p-3 rounded-full bg-soft-background dark:bg-soft-dark-background text-light-text-primary dark:text-dark-text-primary hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-colors disabled:opacity-50"
              >
                <ArrowUp size={16} />
              </button>
            </div>
          )}

          {chatLoadingHistory && page === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <LoaderSpin />
            </div>
          ) : (
            <ChatContent
              chatLoading={chatLoadingHistory}
              messages={myMessages}
            />
          )}
        </div>

        {/* Footer input area */}
        <div className="p-4 bg-white dark:bg-black-900 border-t border-light-input-border dark:border-dark-input-border">
          {typing && (
            <p className="animate-bounce transition-all delay-200 flex items-center gap-1 m-2 text-text-secondary dark:text-dark-text-secondary text-sm">
              <span>{t('typing')}</span>
            </p>
          )}
          <ChatFooter canChat={requestSummary?.payload?.canChat} />
        </div>
      </div>
    </SectionWrapper>
  );
}
